import React from 'react';
import { useGameStore } from '../../store/gameStore';
import { CustomerView } from '../gameplay/CustomerView';
import { ChefView } from '../gameplay/ChefView';
import { getMealPeriodEmoji } from '../../data/menuData';

interface Props {
  send: (action: string, data?: object) => void;
}

const MEAL_PERIODS = ['breakfast', 'lunch', 'snack', 'dinner'];

export function GameplayScreen({ send }: Props) {
  const { role, playerName, currentDay, currentMealPeriod, setPhase, isHost } = useGameStore();
  const mealPeriod = currentMealPeriod || 'breakfast';
  const currentPeriodIndex = MEAL_PERIODS.indexOf(mealPeriod);

  const handleNextPeriod = () => {
    if (currentPeriodIndex < MEAL_PERIODS.length - 1) {
      // Go to next meal period
      send('startMealPeriod', { mealPeriod: MEAL_PERIODS[currentPeriodIndex + 1] });
    } else {
      // End of day
      setPhase('day_end');
    }
  };

  return (
    <div className="screen" style={{ 
      justifyContent: 'flex-start', 
      paddingTop: 80,
    }}>
      {/* Top Bar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'var(--ltc-bg-dark)',
        color: 'var(--ltc-text-inverse)',
        padding: '12px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: 'var(--ltc-border)',
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 24 }}>🍽️</span>
          <span style={{ fontWeight: 600 }}>Little Toasty Crumb Restaurant</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, opacity: 0.7 }}>DAY</div>
            <div style={{ 
              fontFamily: 'var(--ltc-font-display)', 
              fontSize: 16,
              color: 'var(--ltc-accent-glow)',
            }}>
              {currentDay || 1}/7
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, opacity: 0.7 }}>PERIOD</div>
            <div style={{ fontSize: 14, textTransform: 'capitalize' }}>
              {mealPeriod} {getMealPeriodEmoji(mealPeriod)}
            </div>
          </div>
          <div style={{
            background: role === 'chef' ? 'var(--ltc-primary)' : 'var(--ltc-accent)',
            padding: '4px 12px',
            fontSize: 12,
            fontWeight: 600,
          }}>
            {role === 'chef' ? '👨‍🍳 Chef' : '🧑‍💼 Customer'}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ 
        width: '100%', 
        maxWidth: 800,
      }}>
        {role === 'chef' ? (
          <ChefView send={send} />
        ) : (
          <CustomerView send={send} />
        )}

        {/* Period Controls (for testing/demo) */}
        {isHost && (
          <div style={{
            marginTop: 32,
            padding: 16,
            background: 'var(--ltc-bg-secondary)',
            border: 'var(--ltc-border)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 12, color: 'var(--ltc-text-muted)', marginBottom: 8 }}>
              Host Controls
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
              {MEAL_PERIODS.map((period, idx) => (
                <button
                  key={period}
                  onClick={() => send('startMealPeriod', { mealPeriod: period })}
                  style={{
                    padding: '8px 16px',
                    background: period === mealPeriod ? 'var(--ltc-accent)' : 'var(--ltc-bg-card)',
                    color: period === mealPeriod ? 'white' : 'var(--ltc-text-primary)',
                    border: 'var(--ltc-border)',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}
                >
                  {getMealPeriodEmoji(period)} {period}
                </button>
              ))}
              <button
                onClick={handleNextPeriod}
                style={{
                  padding: '8px 16px',
                  background: 'var(--ltc-primary)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {currentPeriodIndex >= MEAL_PERIODS.length - 1 ? '🏁 End Day' : '➡️ Next Period'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
